import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { FC, useEffect, useMemo, useState } from 'react';
import { debounce } from 'underscore';
import useLegendStore from '../../../../../store/legendStore';
import styles from '../../Style/LegendView.module.scss';
import LegendScaleSlider from './LegendScaleSlider';

interface Props {
  result: any;
  setLegendStartEndValues: (data: [number | null, number | null]) => void;
}

export const LegendView: FC<Props> = ({ result, setLegendStartEndValues }) => {
  const legend = useLegendStore((state) => state.legend);
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState(false);
  const [startValue, setStartValue] = useState(0);
  const [endValue, setEndValue] = useState(0);

  const hasValidData = useMemo(() => {
    return result?.featureCollection?.features?.length > 0;
  }, [result?.featureCollection?.features?.length]);

  useEffect(() => {
    if (hasValidData) {
      if (!isEditing) {
        setLegendStartEndValues([result?.minDepth, result?.maxDepth]);
        setStartValue(result?.minDepth);
        setEndValue(result?.maxDepth);
      }
    }
  }, [result?.minDepth, result?.maxDepth, isEditing]);

  const onLegendStartEndChanged = debounce((min: number, max: number) => {
    setLegendStartEndValues([min, max]);
  }, 50);

  const legendValues = useMemo(() => {
    if (!legend?.values) return [];
    const isBathymetry = legend.title.toLowerCase().includes('bathymetry');
    return isBathymetry ? legend.values : [...legend.values].reverse();
  }, [legend]);

  if (!legend) return null;

  return (
    <div className={`${styles.root}`}>
      <div className={styles.header} onClick={() => setShowLegend(!showLegend)}>
        <div className={styles.title}>{legend?.name}</div>
        {showLegend ? (
          <UpOutlined className={`${styles.icon}`} />
        ) : (
          <DownOutlined className={`${styles.icon}`} />
        )}
      </div>
      {showLegend && (
        <div className={styles.content}>
          <div className={styles.contentTitle}>{'[' + legend?.unit + ']'}</div>
          <div className={styles.contentValuesContainer}>
            {legendValues?.map((v, i) => (
              <div key={i} className={styles.contentValueItem}>
                <div className={styles.number}>{v.value.toFixed(2)}</div>
                <div
                  style={{ backgroundColor: v.color }}
                  className={styles.colorBlock}
                ></div>
              </div>
            ))}
          </div>
          {hasValidData && (
            <LegendScaleSlider
              endValue={endValue}
              startValue={startValue}
              setEndValue={setEndValue}
              setStartValue={setStartValue}
              setIsEditing={setIsEditing}
              showLegendScale={true}
              initialLegendValue={{
                legendMinValue: result?.minDepth ?? -10,
                legendMaxValue: result?.maxDepth ?? 9999,
              }}
              onLegendStartEndChanged={onLegendStartEndChanged}
            />
          )}
        </div>
      )}
    </div>
  );
};
